import { useForm, useFieldArray } from 'react-hook-form';

import { CreateTrackableItem, TrackableObjectType } from 'types/trackables';

import { useCreateTrackableItem } from '../hooks/useCreateTrackableItem';
import { TRACKABLE_OBJECT_TYPE_LABELS } from '../utils';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';

interface FormData extends CreateTrackableItem {
  licensePlate: string;
  customAttributes?: Array<{ key: string; value: string }>;
}

export interface AddTrackableItemFormProps {
  trackableListId: string;
  objectType: TrackableObjectType;
  onSuccess?: (createdItemId: string) => void;
}

export function AddTrackableItemForm({
  trackableListId,
  objectType,
  onSuccess,
}: AddTrackableItemFormProps) {
  const itemType = TRACKABLE_OBJECT_TYPE_LABELS[objectType].toLowerCase();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      labels: [],
      licensePlate: '',
      customAttributes: [{ key: '', value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'customAttributes',
  });

  const { mutateAsync: createTrackableItem, isLoading } =
    useCreateTrackableItem();

  const onSubmit = async (data: FormData) => {
    try {
      // Convert comma-separated string to array if needed
      const processedData: CreateTrackableItem = {
        name: data.name,
        description: data.description,
        labels:
          typeof data.labels === 'string'
            ? (data.labels as string)
                .split(',')
                .map((label: string) => label.trim())
                .filter(Boolean)
            : data.labels || [],
        attributes: {},
      };

      // Add license plate to attributes if it's a vehicle
      if (objectType === 'vehicle') {
        processedData.attributes = {
          ...processedData.attributes,
          licensePlate: data.licensePlate?.trim() || '',
        };
      }

      // Add custom attributes if it's a custom item and attributes are provided
      if (objectType === 'custom' && data.customAttributes) {
        const customAttrs = data.customAttributes
          .filter((attr) => attr.key.trim() && attr.value.trim())
          .reduce(
            (acc, attr) => {
              acc[attr.key.trim()] = attr.value.trim();
              return acc;
            },
            {} as Record<string, string>
          );

        processedData.attributes = {
          ...processedData.attributes,
          ...customAttrs,
        };
      }

      const createdItem = await createTrackableItem({
        trackable_list_id: trackableListId,
        ...processedData,
      });

      reset();
      onSuccess?.(createdItem.id);
    } catch (error) {
      // Error is already handled by the mutation hook
      console.error('Failed to create watchlist item:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-4">
        <Field label="Name" required error={errors.name?.message}>
          <Input
            {...register('name', {
              required: `Please enter a name for the ${itemType}.`,
            })}
            placeholder={`Enter ${itemType} name...`}
          />
        </Field>

        <Field label="Description">
          <Input
            {...register('description')}
            placeholder="Enter description (optional)..."
          />
        </Field>

        <Field label="Labels">
          <Input
            {...register('labels')}
            placeholder="Enter labels separated by commas (optional)..."
          />
        </Field>

        {objectType === 'vehicle' && (
          <Field
            label="License Plate"
            required
            error={errors.licensePlate?.message}
          >
            <Input
              {...register('licensePlate', {
                required: 'Please enter a license plate number.',
              })}
              placeholder="Enter license plate number..."
            />
          </Field>
        )}

        {objectType === 'custom' && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Custom Attributes
              </label>
              <Button
                type="button"
                variant="secondary"
                onClick={() => append({ key: '', value: '' })}
              >
                Add Attribute
              </Button>
            </div>
            <div className="space-y-2">
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-start gap-2">
                  <div className="flex-1">
                    <Input
                      {...register(`customAttributes.${index}.key` as const)}
                      placeholder="Key"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      {...register(`customAttributes.${index}.value` as const)}
                      placeholder="Value"
                    />
                  </div>
                  {fields.length > 1 && (
                    <IconButton
                      icon="delete"
                      label="Remove attribute"
                      variant="ghost"
                      onClick={() => remove(index)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <ButtonGroup>
          <Button
            type="submit"
            variant="primary"
            size="small"
            loading={isLoading}
          >
            Add {itemType.charAt(0).toUpperCase() + itemType.slice(1)}
          </Button>
        </ButtonGroup>
      </div>
    </form>
  );
}
